import map from 'lodash/map';
import React from 'react';
import { FieldWithValue } from 'api/tasks';
import { Followers } from 'components/molecules/tasks/followers';
import { useSquad } from 'hooks/use-squad';
import { logger } from 'services';
import { CheckboxField } from './checkbox-field';
import { DateField } from './date-field';
import styles from './editable-cell.module.css';
import { InputNumberField, NumberField } from './input-number-field';
import { InputTextField } from './input-text-field';
import { MultiLineInputTextField } from './multiline-text-field';
import { ProgressField } from './prgoress-field';
import { SelectField } from './select-field';

interface Props {
  title: React.ReactNode;
  editable: boolean;
  children?: React.ReactNode;
  dataIndex: string;
  record: FieldWithValue;
  handleSave: (value, row) => void;
}

const FIELD_TYPE_MULTI_SELECT = 'MULTI_SELECT';
const FIELD_TYPE_SINGLE_SELECT = 'SINGLE_SELECT';
const FIELD_TYPE_TEXT = 'TEXT';
const FIELD_TYPE_MULTI_LINE_TEXT = 'MULTI_LINE_TEXT';
const FIELD_TYPE_NUMBER = 'NUMBER';
const FIELD_TYPE_BOOLEAN = 'BOOLEAN';
const FIELD_TYPE_DATE = 'DATE';
const FIELD_TYPE_USER = 'USER';

const getFieldType = (field: FieldWithValue) =>
  field?.metaData?.options?.length ? field.fieldType : field.fieldDataType;

export const EditableCell: React.FC<Props> = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  // TODO: Use only when required
  // TODO: Stop using implicit dependency on SquadProvider
  const members = useSquad();
  const onChange = (newValue) => {
    handleSave(newValue, { ...record });
  };

  const handleUserSelectChange = (_v, isMultiple = false) => {
    const result = map(_v.users, 'userId').map((id) => id);

    if (isMultiple) {
      onChange(result);
    } else {
      onChange(result[0]);
    }
  };

  let childNode = children;

  if (record) {
    const { value } = record;

    if (editable) {
      const fieldType = getFieldType(record);

      /* NOTE:: It is hacky but it works, right?
      For time being we added this dirty implementation. It can be improved later */
      if (record.fieldName === 'progress') {
        childNode = (
          <ProgressField
            field={record}
            initialValue={value as number}
            onChange={onChange}
          />
        );
      } else {
        /* eslint-disable no-case-declarations */
        switch (fieldType) {
          case FIELD_TYPE_TEXT:
            childNode = (
              <InputTextField
                isEdit
                initialValue={value as string}
                onChange={onChange}
              />
            );
            break;
          case FIELD_TYPE_MULTI_LINE_TEXT:
            childNode = (
              <MultiLineInputTextField
                initialValue={value as string}
                onChange={onChange}
              />
            );
            break;
          case FIELD_TYPE_NUMBER:
            childNode = (
              <InputNumberField
                field={record as NumberField}
                initialValue={value as number}
                onChange={onChange}
              />
            );
            break;
          case FIELD_TYPE_DATE:
            childNode = (
              <DateField
                className={styles.date_picker}
                field={record}
                onChange={handleSave}
                initialValue={record.value as number}
              />
            );
            break;
          case FIELD_TYPE_BOOLEAN:
            childNode = <CheckboxField field={record} onChange={onChange} />;
            break;
          case FIELD_TYPE_SINGLE_SELECT:
            if (record.fieldName === 'Priority') {
              logger.log('priority field', record);
            }

            childNode = (
              <SelectField
                field={record}
                value={[value as any as number]}
                onChange={handleSave}
              />
            );
            break;
          case FIELD_TYPE_MULTI_SELECT:
            childNode = (
              <SelectField
                mode="multiple"
                field={record}
                value={value as any as number[]}
                onChange={handleSave}
              />
            );
            break;
          case FIELD_TYPE_USER:
            // TODO:: support user type field
            const v = record.value as Array<string> | string;
            const isMultiple = record.fieldType === FIELD_TYPE_MULTI_SELECT;
            let userIds: number[];

            if (Array.isArray(v)) {
              userIds = v.map((_v) => Number(_v));
            } else {
              userIds = v ? [Number(v)] : [];
            }
            const selectedUsers = (members.users ?? []).filter((u) =>
              userIds.includes(u.userId),
            );

            /* eslint-enable no-case-declarations */
            childNode = (
              <Followers
                size="small"
                type={isMultiple ? 'multiple' : undefined}
                placeholder="—"
                value={{ users: selectedUsers }}
                dropdownHeaderLabel={record.fieldName}
                onChange={(_v) => handleUserSelectChange(_v, isMultiple)}
                options={{ users: members.users }}
              />
            );
            break;
          default:
            childNode = <></>;
        }
      }
    }
  }

  return <td {...restProps}>{childNode}</td>;
};
